import {getTestProps} from '@benchling/js-test-utils';
import {Box, createStyles, Tabs} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import {assert} from 'Asserts';
import navigateToURL, {getPreviousURL} from 'lib/navigateToURL';
import ChatSettingsAgentContent from 'llm/chat-library/agents/ChatSettingsAgentContent';
import ChatSettingsConnectorContent from 'llm/chat-library/connectors/ChatSettingsConnectorContent';
import ChatSettingsGeneralContent from 'llm/chat-library/shared/list-detail/ChatSettingsGeneralContent';
import {
  AgentsGroup,
  ChatSettingsModalState,
  ChatSettingsTab,
  closeChatSettingsModal,
  ConnectorsGroup,
  openChatSettingsModal,
  SkillsGroup,
  useAISidepanelGlobalClientState,
} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import ChatSettingsSkillContent from 'llm/chat-library/skills/ChatSettingsSkillContent';
import ChatSettingsUseCasesContent from 'llm/chat-library/use-cases/ChatSettingsUseCasesContent';
import {isAgentsFeatureEnabled} from 'llm/isAgentsFeatureEnabled';
import {isMcpAiConnectorsFeatureEnabled} from 'llm/isMcpAiConnectorsFeatureEnabled';
import {chatSettingsModalUrl, isChatSettingsModalPath} from 'llm/routes/UrlUtils';
import useOnMount from 'ui-library/hooks/useOnMount';
import ModalAnchor from 'ui-library/ModalAnchor';
import Modal from 'ui-library/modals/Modal';
import enumIncludes from 'util/enumIncludes';
import * as WindowLocationUtil from 'util/WindowLocationUtil';

const useStyles = createStyles((theme) => ({
  body: {
    display: 'flex',
    flexDirection: 'row',
    height: '70vh',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabsRoot: {
    display: 'flex',
    flexDirection: 'row',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabsList: {
    width: 200,
    minWidth: 200,
    padding: theme.spacing.md,
    borderRight: `1px solid ${theme.colors.gray[3]}`,
    gap: theme.spacing.xs,
  },
  tabsPanel: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
}));

/**
 * The settings tabs, in display order. `enabled` gates whether a tab is shown for this tenant; add a
 * new tab by appending an entry with its own predicate rather than special-casing the filter below.
 */
const TABS: Array<{value: ChatSettingsTab; label: string; enabled: () => boolean}> = [
  {value: ChatSettingsTab.GENERAL, label: 'General', enabled: () => true},
  // Agents sits second to match the chat sidebar, where it follows New Chat.
  {
    value: ChatSettingsTab.AGENTS,
    label: 'Agents',
    enabled: isAgentsFeatureEnabled,
  },
  {value: ChatSettingsTab.SKILLS, label: 'Skills', enabled: () => true},
  {value: ChatSettingsTab.CONNECTORS, label: 'AI Connectors', enabled: isMcpAiConnectorsFeatureEnabled},
  {value: ChatSettingsTab.USE_CASES, label: 'Use cases', enabled: () => true},
];

/**
 * Mount/unmount wrapper for the chat settings modal.
 *
 * The URL is the source of truth for whether the modal is open: the AIRoutes handlers write the
 * URL-derived state into global client state on navigation *into* a settings URL, and this wrapper
 * mirrors it. It mounts the modal only while that state is present so the modal's mount/unmount
 * lines up with the URL, and clears the state when the user navigates *away* (e.g. browser back).
 */
export function ChatSettingsModalContainer({
  onRestartOnboarding,
}: {
  /** Restarts onboarding from the General tab; the parent closes the modal as part of restarting. */
  onRestartOnboarding?: () => void;
} = {}): JSX.Element | null {
  const [panelState] = useAISidepanelGlobalClientState();
  const modalState = panelState.chatSettingsModal;
  const isOpen = modalState != null;

  // Keep the modal mounted/unmounted in lockstep with the URL when the user navigates with the
  // browser (back/forward). The modal's open state lives in global client state, written by the
  // AIRoutes handlers on navigation *into* a settings URL; nothing else reacts to navigation *away*.
  // If we're still open but the URL is no longer a settings route, the user navigated away (e.g. hit
  // back), so mirror that by clearing the modal state. Routing back *into* the modal is handled by
  // the AIRoutes handlers firing on popstate, so we only need to handle the "navigated away" case.
  useOnMount(() => {
    const handlePopState = (): void => {
      if (!isChatSettingsModalPath(WindowLocationUtil.getPathName())) {
        closeChatSettingsModal();
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  });

  return (
    <ModalAnchor open={isOpen}>
      {/* Mount the content only while open so its mount/unmount lines up with the modal opening and
          closing; that lets it snapshot the origin URL on open and restore it on a UI-driven close. */}
      {modalState != null ? (
        <ChatSettingsModal modalState={modalState} onRestartOnboarding={onRestartOnboarding} />
      ) : undefined}
    </ModalAnchor>
  );
}

/**
 * The chat settings modal itself. Rendered by {@link ChatSettingsModalContainer} while the modal is
 * open (`modalState != null`). Exported for testing so the tab/subview interactions can be exercised
 * directly with a supplied `modalState`, without the async `ModalAnchor`/`LegacyModalContainer` path.
 */
export function ChatSettingsModal({
  modalState,
  onRestartOnboarding,
}: {
  modalState: ChatSettingsModalState;
  onRestartOnboarding?: () => void;
}): JSX.Element {
  const {classes} = useStyles();

  useOnMount(() => {
    const previousUrl = getPreviousURL();
    const urlToRestore = previousUrl.startsWith('/') ? previousUrl : '/editor';

    return () => {
      // On unmount, only restore the origin URL if the URL still points at the modal. If it no
      // longer does, the unmount was caused by a navigation that already moved the address bar
      // (e.g. browser back), so overwriting it here would fight that navigation.
      if (isChatSettingsModalPath(WindowLocationUtil.getPathName())) {
        navigateToURL(urlToRestore, {replace: true, skipTrigger: true});
      }
    };
  });

  const handleClose = useCallback(() => {
    // Clear the modal state; unmounting this component runs the cleanup above, which restores the
    // address bar to the URL the modal was opened over.
    closeChatSettingsModal();
  }, []);

  // Bumped to remount the Agents content, discarding its in-content sub-view (see handleTabChange).
  const [agentsRemountKey, setAgentsRemountKey] = useState(0);

  const handleTabChange = useCallback(
    (tab: string | null) => {
      assert(
        tab != null,
        'Unexpectedly tried to deactivate tab in ChatSettingsModal without allowTabDeactivation'
      );
      assert(enumIncludes(ChatSettingsTab, tab), `Unexpected chat settings navigation, tab: ${tab}`);

      // Clicking a tab should always land on that tab's listing root. Mantine fires this even when the
      // clicked tab is already active, but navigating to the URL it's already on is a no-op. Skills and
      // Connectors detail views are URL-driven (their selected item lives in the URL), so returning to
      // the base URL resets them. The Agents create form is local component state, so re-clicking the
      // already-active Agents tab wouldn't discard it; remount the Agents content to return to a clean
      // listing, mirroring the state you'd get by leaving and re-entering the tab.
      if (tab === ChatSettingsTab.AGENTS && modalState.tab === ChatSettingsTab.AGENTS) {
        setAgentsRemountKey((key) => key + 1);
      }

      navigateToURL(chatSettingsModalUrl(tab), {replace: true});
    },
    [modalState.tab]
  );

  const handleSelectItem = useCallback(
    (itemId: string) => {
      navigateToURL(chatSettingsModalUrl(modalState.tab, {selectedItemId: itemId}));
    },
    [modalState.tab]
  );

  const handleBack = useCallback(() => {
    // Back to the listing: same tab, no selected item. Replace so it doesn't stack history.
    navigateToURL(chatSettingsModalUrl(modalState.tab), {replace: true});
  }, [modalState.tab]);

  // The group filter is owned by modal state, not the URL (it can go stale, e.g. an item moved to
  // another group). Switching the group tab returns to the listing (no selected item) for that group.
  const handleSkillsGroupChange = useCallback((group: SkillsGroup) => {
    openChatSettingsModal({tab: ChatSettingsTab.SKILLS, selectedItemId: null, group});
  }, []);

  const handleConnectorsGroupChange = useCallback((group: ConnectorsGroup) => {
    openChatSettingsModal({tab: ChatSettingsTab.CONNECTORS, selectedItemId: null, group});
  }, []);

  const handleAgentsGroupChange = useCallback((group: AgentsGroup) => {
    openChatSettingsModal({tab: ChatSettingsTab.AGENTS, selectedItemId: null, group});
  }, []);

  return (
    <Modal
      title="AI settings"
      onRequestClose={handleClose}
      closeableOnBackgroundClick={true}
      large={true}
      bodyPadded={false}
      scrollable={false}
      dataTestAttrs={{component: 'ChatSettingsModal', element: 'modal'}}
    >
      <Box className={classes.body}>
        <Tabs
          orientation="vertical"
          value={modalState.tab}
          onTabChange={handleTabChange}
          className={classes.tabsRoot}
        >
          <Tabs.List className={classes.tabsList}>
            {TABS.filter((tab) => tab.enabled()).map((tab) => (
              <Tabs.Tab
                key={tab.value}
                value={tab.value}
                {...getTestProps({component: 'ChatSettingsModal', element: 'navItem', key: tab.value})}
                data-amp-safe={false}
              >
                {tab.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Tabs.Panel value={ChatSettingsTab.GENERAL} className={classes.tabsPanel}>
            {modalState.tab === ChatSettingsTab.GENERAL && (
              <ChatSettingsGeneralContent onRestartOnboarding={onRestartOnboarding} />
            )}
          </Tabs.Panel>

          <Tabs.Panel value={ChatSettingsTab.SKILLS} className={classes.tabsPanel}>
            {modalState.tab === ChatSettingsTab.SKILLS && (
              <ChatSettingsSkillContent
                selectedItemId={modalState.selectedItemId}
                group={modalState.group}
                onSelectItem={handleSelectItem}
                onGroupChange={handleSkillsGroupChange}
                onBack={handleBack}
                onClose={handleClose}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value={ChatSettingsTab.CONNECTORS} className={classes.tabsPanel}>
            {modalState.tab === ChatSettingsTab.CONNECTORS && (
              <ChatSettingsConnectorContent
                group={modalState.group}
                onGroupChange={handleConnectorsGroupChange}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value={ChatSettingsTab.AGENTS} className={classes.tabsPanel}>
            {modalState.tab === ChatSettingsTab.AGENTS && (
              <ChatSettingsAgentContent
                key={`${agentsRemountKey}-${modalState.createNew === true ? 'create' : 'list'}`}
                selectedItemId={modalState.selectedItemId}
                group={modalState.group}
                initiallyCreating={modalState.createNew}
                onSelectItem={handleSelectItem}
                onGroupChange={handleAgentsGroupChange}
                onBack={handleBack}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value={ChatSettingsTab.USE_CASES} className={classes.tabsPanel}>
            {modalState.tab === ChatSettingsTab.USE_CASES && <ChatSettingsUseCasesContent />}
          </Tabs.Panel>
        </Tabs>
      </Box>
    </Modal>
  );
}
